import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Play, Pause, RotateCcw, Loader2, BookmarkPlus, Check, MessageCircle, Clapperboard } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { srDisplay } from "@/lib/serbianScript";
import { langDisplayName } from "@/lib/languages";

const BASE = process.env.REACT_APP_BACKEND_URL;
const newTurn = () => (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`);
const RTL = ["ar", "he", "fa", "ur"];
// Localized UI label for a moment title/role; personal names (no key) fall back to the stored text.
const vmLabel = (t, key, fallback) => { const v = t(key); return v === key ? fallback : v; };

// 8.3.11 Video Moment — Watch. Understand. Respond. mode="respond" (default) or "briefing" (opens a Mission).
export function VideoMoment({ moment, mode = "respond", onStart, extraAction }) {
  const { t } = useI18n();
  const { user } = useAuth();
  const disp = (s) => (moment.target_language === "sr" ? srDisplay(s, user?.target_script === "latin" ? "latin" : "cyrillic") : s);
  const title = vmLabel(t, `vm.titles.${moment.id}`, moment.title);
  return (
    <section className="nb-card p-4 md:p-5" data-testid={`video-moment-${moment.id}`}>
      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest rtl:tracking-normal text-gray-500">
        <Clapperboard className="w-4 h-4" /> {t("vm.label")} · <span title={t("vm.recommendedLevel")} aria-label={`${t("vm.recommendedLevel")}: ${moment.cefr_band}`} data-testid="vm-level">{moment.cefr_band}</span>
      </div>
      <h3 className="font-cabinet font-extrabold text-xl mt-1" data-testid="vm-title">{title}</h3>
      <Player moment={moment} t={t} title={title} />
      <Reveals moment={moment} disp={disp} t={t} />
      {mode === "briefing"
        ? <button onClick={onStart} className="nb-btn-primary w-full mt-4 py-3" data-testid="vm-start-mission">{t("vm.startMission")}</button>
        : <Respond moment={moment} disp={disp} t={t} extraAction={extraAction} />}
    </section>
  );
}

// Overlay lines start at the video times where each speaker talks (media.line_starts), so voice follows the picture.
function Player({ moment, t, title }) {
  const vid = useRef(null);
  const clips = useRef(null);
  const cur = useRef(null);
  const next = useRef(0);
  const [playing, setPlaying] = useState(false);
  const [failed, setFailed] = useState(false);
  const lines = moment.media.lines;
  const overlay = moment.audio_mode === "overlay" && lines.length > 0;
  const starts = moment.media.line_starts?.length === lines.length ? moment.media.line_starts : lines.map((_, i) => i * 3);
  // Line clips are fetched on first Play (not page load) so each one can start the instant its cue is reached.
  const load = () => {
    if (!overlay || clips.current) return;
    clips.current = lines.map((u) => { const a = new Audio(BASE + u); a.preload = "auto"; a.onended = () => { if (vid.current?.ended) setPlaying(false); }; return a; });
  };
  const onTime = () => {
    const i = next.current;
    if (!overlay || !clips.current || i >= lines.length || vid.current.currentTime < starts[i]) return;
    next.current = i + 1;
    cur.current?.pause();
    cur.current = clips.current[i];
    cur.current.currentTime = 0;
    cur.current.play().catch(() => {}); // audio failure never blocks: transcript stays available
  };
  const audioBusy = () => overlay && cur.current && !cur.current.ended;
  const reset = () => { next.current = 0; cur.current?.pause(); cur.current = null; };
  const toggle = () => {
    const v = vid.current;
    if (playing) { v.pause(); cur.current?.pause(); setPlaying(false); return; }
    load();
    if (v.ended) { v.currentTime = 0; reset(); }
    v.play().then(() => { setPlaying(true); if (audioBusy()) cur.current.play().catch(() => {}); }).catch(() => setFailed(true));
  };
  const replay = () => {
    const v = vid.current; load(); v.currentTime = 0; reset();
    v.play().then(() => setPlaying(true)).catch(() => setFailed(true));
  };
  useEffect(() => {  // frame-accurate cue check while playing (timeupdate alone fires only ~4×/s)
    if (!playing || !overlay) return undefined;
    let id;
    const tick = () => { onTime(); id = requestAnimationFrame(tick); };
    id = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(id);
  }, [playing]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => { cur.current?.pause(); }, []);
  if (failed) return <div role="status" className="mt-3 p-3 rounded-xl border-2 border-ink bg-butter/50 text-sm" data-testid="vm-video-failed">{t("vm.videoFailed")}
    <button onClick={() => { reset(); setPlaying(false); setFailed(false); }} className="nb-btn-outline py-1 px-3 text-xs mt-2 flex w-fit items-center gap-1" data-testid="vm-retry-video">
      <RotateCcw className="w-3 h-3" /> {t("vm.retryVideo")}</button></div>;
  return (
    <div className="mt-3">
      <div className="relative mx-auto w-full max-w-sm aspect-[4/5] overflow-hidden rounded-2xl border-2 border-ink bg-ink">
        <video ref={vid} src={BASE + moment.media.video} poster={BASE + moment.media.poster} preload="none" playsInline muted={overlay}
          aria-label={`${t("vm.video")}: ${title}`} className="absolute inset-0 w-full h-full object-cover" onTimeUpdate={onTime}
          onEnded={() => { if (!audioBusy()) setPlaying(false); }}
          onError={() => setFailed(true)} data-testid="vm-video" />
      </div>
      <div className="flex justify-center gap-2 mt-3">
        <button onClick={toggle} className="nb-btn-primary py-2 px-4" aria-pressed={playing} data-testid="vm-play">
          {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 rtl:-scale-x-100" />} {t(playing ? "vm.pause" : "vm.play")}</button>
        <button onClick={replay} className="nb-btn-outline py-2 px-4" data-testid="vm-replay"><RotateCcw className="w-4 h-4" /> {t("vm.replay")}</button>
      </div>
    </div>
  );
}

function Reveals({ moment, disp, t }) {
  const [tr, setTr] = useState(false);
  const [mn, setMn] = useState(false);
  const role = Object.fromEntries(moment.speakers.map((s) => [s.id, vmLabel(t, `vm.roles.${s.role}`, s.role)]));
  const dir = RTL.includes(moment.target_language) ? "rtl" : "ltr";
  return (
    <div className="mt-3">
      <div className="flex flex-wrap gap-2">
        <button onClick={() => setTr(!tr)} aria-expanded={tr} className="nb-btn-outline py-1.5 px-3 text-sm" data-testid="vm-toggle-transcript">{t(tr ? "vm.hideTranscript" : "vm.showTranscript")}</button>
        {moment.meaning && <button onClick={() => setMn(!mn)} aria-expanded={mn} className="nb-btn-outline py-1.5 px-3 text-sm" data-testid="vm-toggle-meaning">{t(mn ? "vm.hideMeaning" : "vm.showMeaning")}</button>}
      </div>
      {tr && <ol className="mt-3 space-y-1.5" lang={moment.target_language} dir={dir} data-testid="vm-transcript">
        {moment.transcript.map((l, i) => <li key={i} dir={dir} className="text-start"><bdi className="text-xs font-bold text-gray-500">{role[l.speaker]}</bdi>: <bdi className="font-semibold">{disp(l.text)}</bdi>
          {mn && <div className="text-sm text-gray-600" data-testid={`vm-meaning-${i}`}><bdi>{moment.meaning[i]}</bdi></div>}</li>)}
      </ol>}
      {!tr && mn && <ol className="mt-3 space-y-1 text-sm text-gray-600" dir="auto" data-testid="vm-meaning">{moment.meaning.map((m, i) => <li key={i}>{m}</li>)}</ol>}
    </div>
  );
}

function Respond({ moment, disp, t, extraAction }) {
  const { locale } = useI18n();
  const navigate = useNavigate();
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const [saved, setSaved] = useState({});
  const turn = useRef(newTurn());
  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || busy) return;
    setBusy(true);
    try {
      const r = await api.post(`/video-moments/${moment.id}/respond`, { text: text.trim(), turn_id: turn.current });
      setRes({ ...r.data, mine: text.trim() }); turn.current = newTurn();
    } catch { toast.error(t("vm.respondFailed")); } finally { setBusy(false); }
  };
  const save = async (i) => {
    try { await api.post(`/video-moments/${moment.id}/phrases/save`, { index: i }); setSaved((s) => ({ ...s, [i]: true })); }
    catch { toast.error(t("vm.respondFailed")); }
  };
  return (
    <div className="mt-4">
      <p className="font-bold" data-testid="vm-question">{t("vm.whatWouldYouSay")} <bdi lang={moment.target_language} className="text-gray-600 font-normal">{disp(moment.prompt)}</bdi></p>
      {!res ? <form onSubmit={send} className="mt-2">
        <label htmlFor={`vm-in-${moment.id}`} className="sr-only">{t("vm.whatWouldYouSay")}</label>
        <textarea id={`vm-in-${moment.id}`} value={text} onChange={(e) => setText(e.target.value)} rows={2} dir="auto" lang={moment.target_language} maxLength={400}
          placeholder={t("vm.placeholder", { lang: langDisplayName(moment.target_language, locale) })}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) send(e); }}
          className="w-full border-2 border-ink rounded-xl px-3 py-3 text-lg bg-white focus:outline-none focus:ring-4 focus:ring-coral/30" data-testid="vm-input" />
        <button type="submit" disabled={!text.trim() || busy} className="nb-btn-primary w-full mt-2 py-3 disabled:opacity-50" data-testid="vm-respond">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <MessageCircle className="w-4 h-4 rtl:-scale-x-100" />} {t("vm.respond")}</button>
      </form> : <div role="status" aria-live="polite" className="mt-2 p-3 rounded-xl border-2 border-ink bg-white" data-testid="vm-result">
        <p dir="auto"><bdi className="font-semibold">{res.mine}</bdi></p>
        <p className="mt-2 font-bold" data-testid={res.verdict === "better" ? "vm-better" : "vm-natural"}>{t(res.verdict === "better" ? "vm.better" : "vm.natural")}</p>
        {res.better && <p dir="auto" lang={moment.target_language} className="font-semibold text-coral">{disp(res.better)}</p>}
        {res.note && <p dir="auto" className="text-sm text-gray-600 mt-1">{res.note}</p>}
      </div>}
      {res && <div className="mt-4">
        <div className="text-xs font-bold uppercase tracking-widest rtl:tracking-normal text-gray-500">{t("vm.usefulPhrases")}</div>
        <ul className="mt-2 space-y-2">{moment.phrases.slice(0, 3).map((p, i) => <li key={i} className="flex items-center justify-between gap-2">
          <bdi dir="auto" lang={moment.target_language} className="font-semibold">{disp(p)}</bdi>
          <button onClick={() => save(i)} disabled={saved[i]} className="nb-btn-outline py-1 px-2 text-xs shrink-0" data-testid={`vm-save-${i}`}>
            {saved[i] ? <><Check className="w-3 h-3" /> {t("vm.saved")}</> : <><BookmarkPlus className="w-3 h-3" /> {t("vm.savePhrase")}</>}</button></li>)}</ul>
        <div className="flex flex-wrap gap-2 mt-4">
          <button onClick={() => navigate("/friend")} className="nb-btn-outline py-2 px-3 text-sm" data-testid="vm-continue-friend">{t("vm.continueFriend")}</button>
          {extraAction}
        </div>
      </div>}
    </div>
  );
}

// Today card: at most one approved moment for the active language; renders nothing otherwise.
export function TodayVideoMoment() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [m, setM] = useState(null);
  useEffect(() => { api.get("/video-moments/today").then((r) => setM(r.data.moment)).catch(() => {}); }, []);
  if (!m) return null;
  const interview = m.scenario === "job_interview" &&
    <button onClick={() => navigate(`/friend?interview=1&context=${encodeURIComponent(vmLabel(t, `vm.titles.${m.id}`, m.title))}`)} className="nb-btn-outline py-2 px-3 text-sm" data-testid="vm-open-interview">{t("vm.openInterview")}</button>;
  return <div className="mt-6" data-testid="today-video-moment"><VideoMoment moment={m} extraAction={interview} /></div>;
}
